// src/pages/pannel/editor/Editor.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Uploads from './Uploads';
import Configure from './Configure';
import Export from './Export';
import UserProfilePopover from '../../../components/UserProfilePopover';
import projectService from './Configure/services/projectService';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileSheetWrapper from './Configure/mobile/MobileSheetWrapper';
import VideoGuideModal from './Configure/components/VideoGuideModal';
import { VIDEO_GUIDE_DESKTOP_URL, VIDEO_GUIDE_MOBILE_URL, FLOOR_PLAN_MAKER_TUTORIAL_URL } from './Configure/constants/videoGuide';
import { Video as VideoGuideIcon } from 'lucide-react';

export default function Editor() {
  const { projectId: rawProjectId } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isMobile  = useIsMobile();

  const isNewProject = !rawProjectId || rawProjectId === 'new';
  const projectId    = isNewProject ? null : rawProjectId;

  // Session keys scoped to this project slot
  const SESSION_TAB = `editor_tab_${rawProjectId || 'new'}`;
  const SESSION_IMG = `editor_img_${rawProjectId || 'new'}`;

  const getInitialTab = () => {
    if (!isNewProject) return 'configure';
    const saved = sessionStorage.getItem(SESSION_TAB);
    if (saved === 'configure' && sessionStorage.getItem(SESSION_IMG)) return 'configure';
    return 'uploads';
  };

  const getInitialImage = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_IMG);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return null;
  };

  // For UUID-based projects, check if there's a pending floor plan from auto-create
  const getPendingFloorPlan = () => {
    if (!rawProjectId || rawProjectId === 'new') return null;
    try { return sessionStorage.getItem(`pending_fp_${rawProjectId}`) || null; } catch (e) { return null; }
  };

  const [activeTab, setActiveTab]     = useState(getInitialTab);
  const savedImg                      = getInitialImage();
  const pendingFp                     = getPendingFloorPlan();
  const [finalImage, setFinalImage]   = useState(pendingFp ?? savedImg?.url ?? null);
  const [imageData, setImageData]     = useState(savedImg?.data ?? null);
  const [projectName, setProjectName] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);

  // saveRef is populated by Configure on every render so Editor can trigger save
  const saveRef = useRef(null);
  // Export popover (top bar, next to Save): PDF / Word are produced by Configure via saveRef.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const exportWrapRef = useRef(null);
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onDown = (ev) => { if (exportWrapRef.current && !exportWrapRef.current.contains(ev.target)) setExportOpen(false); };
    const onKey = (ev) => { if (ev.key === 'Escape') setExportOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [exportOpen]);
  const runExport = async (kind) => {
    setExportOpen(false);
    if (exportBusy) return;
    setExportBusy(true);
    try {
      if (kind === 'word') await saveRef.current?.exportWord?.();
      else await saveRef.current?.exportPdf?.();
    } finally {
      setExportBusy(false);
    }
  };

  // Custom unsaved-changes modal (no data-router required)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  // Desktop / Mobile / Floor Plan Maker video popups — their buttons live in the top bar.
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoKind, setVideoKind] = useState('desktop');
  const openVideo = (kind) => { setVideoKind(kind); setVideoOpen(true); };
  const pendingNavRef = useRef(null); // stores () => void to execute on confirm
  // Set true the moment the user confirms "Discard & Leave" / "Save & Leave".
  // editorDirty is owned by Configure and only flips back after a save round-trip,
  // so without this flag the popstate guard below would re-intercept the very
  // navigate(-1) that Discard fires and pop the modal straight back up — the
  // "Discard doesn't work sometimes" report (only reproduces via the browser
  // Back button, since that path uses navigate(-1) through the guarded history).
  const isLeavingRef = useRef(false);

  // Track dirty state reported by Configure
  const handleDirtyChange = useCallback((dirty) => {
    setEditorDirty(!!dirty);
  }, []);

  // Guarded navigate — shows unsaved modal before leaving when dirty
  const guardedNavigate = useCallback((path, opts) => {
    if (editorDirty && !isLeavingRef.current) {
      pendingNavRef.current = () => navigate(path, opts);
      setShowUnsavedModal(true);
    } else {
      navigate(path, opts);
    }
  }, [editorDirty, navigate]);

  // Intercept browser back / forward button when dirty
  useEffect(() => {
    if (!editorDirty) return;
    // Push a marker state so the next popstate fires before we actually leave
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      // The user already confirmed they want to leave — let this popstate through.
      if (isLeavingRef.current) return;
      // Push again to stay put, then show modal. This leaves exactly ONE marker
      // entry sitting above the real editor URL in the history stack.
      window.history.pushState(null, '', window.location.href);
      // So to actually go "back" the user has to unwind BOTH that marker and the
      // editor entry itself → navigate(-2). A plain navigate(-1) only pops the
      // marker and dumps them right back on the editor (the "Discard did nothing"
      // bug when triggered via the browser Back button).
      pendingNavRef.current = () => navigate(-2);
      setShowUnsavedModal(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [editorDirty, navigate]);

  // Warn browser on tab close / refresh when dirty
  useEffect(() => {
    const handler = (e) => {
      if (editorDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editorDirty]);

  // Clear stale new-project session keys and consume pending floor plan from sessionStorage
  useEffect(() => {
    if (!isNewProject && rawProjectId) {
      sessionStorage.removeItem('editor_tab_new');
      sessionStorage.removeItem('editor_img_new');
      // pendingFp was already read into initial state; just clean up the key
      sessionStorage.removeItem(`pending_fp_${rawProjectId}`);
    }
  }, [isNewProject, rawProjectId]); // eslint-disable-line

  // An existing project never shows the upload step. Needed because moving from
  // /editor/new to /editor/:id after the auto-create does NOT remount this
  // component (separate <Route>s), so activeTab would otherwise stay 'uploads'.
  useEffect(() => {
    if (!isNewProject && activeTab === 'uploads') setActiveTab('configure');
  }, [isNewProject, activeTab]);

  const newProjectName        = location.state?.newProjectName        ?? null;
  const newProjectDescription = location.state?.newProjectDescription ?? null;
  // Property/Client Details collected on ProjectsPage's "New Project" form —
  // carried through so the very first projectService.create() call below
  // (the actual DB row for this project) saves it straight into
  // config.coverPage.clientDetails, instead of the user having to enter it
  // again by hand on the Cover Page later.
  const newProjectClientDetails = location.state?.newProjectClientDetails ?? null;
  const stateFloorPlanRef     = location.state?.floorPlanRef          ?? null;
  const stateImageData        = location.state?.imageData             ?? null;

  const tabs = isNewProject
    ? [{ id: 'uploads' }, { id: 'configure' }, { id: 'export' }]
    : [{ id: 'configure' }, { id: 'export' }];

  // Guards against double-submit: without this, clicking "Apply Crop & Continue"
  // twice quickly (e.g. because the redirect below used to feel like nothing
  // happened) fired projectService.create() twice and left two duplicate draft
  // projects in the DB for a single upload.
  const isCreatingProjectRef = useRef(false);

  const handleNext = useCallback(async (overrideImage) => {
    const idx = tabs.findIndex((t) => t.id === activeTab);
    if (idx >= tabs.length - 1) return;
    const nextTab = tabs[idx + 1].id;

    // Uploads passes the image it just produced (e.g. a freshly cropped data: URL)
    // directly as an argument. Falling back to the `finalImage` state is NOT safe here:
    // Uploads calls onImageUpdate() (→ setFinalImage) and then onNext() back-to-back:
    // the state update hasn't been committed/re-rendered yet, so `finalImage` in this
    // closure can still be the PRE-crop image. Using the explicit argument sidesteps
    // that stale-closure race entirely (this was the "crop applies but the canvas shows
    // the uncropped image" bug).
    const imageToUse = overrideImage ?? finalImage;

    // Upload → Configure: create the project (floor plan only, draft config), then
    // continue straight into that project's Configure screen so the user can actually
    // set up polygon/measurements/etc and save real data — previously this redirected
    // to /projects instead, leaving only an empty draft behind (looked like "data erased"
    // when reopened, since nothing had ever actually been configured/saved).
    if (activeTab === 'uploads' && nextTab === 'configure' && isNewProject && imageToUse) {
      if (isCreatingProjectRef.current) return; // already creating — ignore repeat clicks
      isCreatingProjectRef.current = true;
      try {
        const name = newProjectName || 'Untitled Project';
        const res  = await projectService.create({
          name,
          description: newProjectDescription || '',
          floorPlanRef: imageToUse,
          unit: 'ft',
          pixelsPerUnit: null,
          config: {
            _draft: true,
            ...(newProjectClientDetails ? { coverPage: { clientDetails: newProjectClientDetails } } : {}),
          },
        });
        const uuid = res?.data?.id ?? res?.id;
        if (uuid && imageToUse) {
          try { sessionStorage.setItem(`pending_fp_${uuid}`, imageToUse); } catch (e) {}
        }
        sessionStorage.removeItem(SESSION_TAB);
        sessionStorage.removeItem(SESSION_IMG);
        if (uuid) {
          setActiveTab('configure');
          navigate(`/editor/${uuid}`, { replace: true });
        } else {
          navigate('/projects', { replace: true, state: { justCreated: name } });
        }
        return;
      } catch (e) {
        console.error('Auto-create failed:', e);
        alert('Could not create the project. Please try again.');
        isCreatingProjectRef.current = false;
      }
      return;
    }

    setActiveTab(nextTab);
    sessionStorage.setItem(SESSION_TAB, nextTab);
  }, [activeTab, tabs, isNewProject, finalImage, imageData, newProjectName, newProjectDescription, newProjectClientDetails, location.state, navigate, SESSION_TAB, SESSION_IMG]);

  const handleImageUpdate = (url, data) => {
    setFinalImage(url);
    setImageData(data);
    try {
      sessionStorage.setItem(SESSION_IMG, JSON.stringify({ url, data }));
    } catch (e) {}
  };

  const handleProjectLoad = (name) => setProjectName(name);

  const topbarLabel = projectName || newProjectName || (isNewProject ? 'New Project' : '…');

  const handleSave = () => {
    if (activeTab !== 'configure') {
      setActiveTab('configure');
      // let Configure mount then save
      setTimeout(() => saveRef.current?.save(), 120);
      return;
    }
    saveRef.current?.save();
  };

  // Ctrl/Cmd+S → save the project (same path as the Save button).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        if (activeTab === 'configure') saveRef.current?.save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTab]);

  // Esc → back to the project list (with the unsaved-changes prompt when dirty).
  // Stays out of the way of anything that is already using Esc: open modals /
  // editors (Configure reports them via hasOpenOverlay), text fields, and
  // handlers that already called preventDefault.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (showUnsavedModal) {
        pendingNavRef.current = null;
        setShowUnsavedModal(false);
        return;
      }
      if (activeTab !== 'configure') return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      if (saveRef.current?.hasOpenOverlay?.()) return;
      guardedNavigate('/projects');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTab, showUnsavedModal, guardedNavigate]);

  const configureProps = {
    finalImage,
    imageData,
    onNext: handleNext,
    projectId,
    initialProjectName: newProjectName,
    initialProjectDescription: newProjectDescription,
    initialProjectClientDetails: newProjectClientDetails,
    onFloorPlanLoad: handleImageUpdate,
    onProjectLoad: handleProjectLoad,
    saveRef,
    onDirtyChange: handleDirtyChange,
    // Mobile only — the "Back Projects" button now lives inside Configure's
    // own bottom toolbar/settings sheet instead of the (hidden-on-mobile)
    // topbar above. Save and the project name don't need threading down
    // separately: Configure already owns both internally (same saveRef.save
    // the topbar button called, and its own current-project state).
    onBackToProjects: () => guardedNavigate('/projects'),
  };

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'uploads':   return <Uploads onNext={handleNext} onImageUpdate={handleImageUpdate} />;
      case 'configure': return <Configure {...configureProps} />;
      case 'export':    return <Export finalImage={finalImage} imageData={imageData} />;
      default:
        return isNewProject
          ? <Uploads onNext={handleNext} onImageUpdate={handleImageUpdate} />
          : <Configure {...configureProps} />;
    }
  };

  return (
    <>
      <div className="editor-container">

        {/* ── Topbar — hidden on mobile; Back/project name/Unsaved badge/
            Save all move into the settings popover instead (see
            <UserProfilePopover> below), freeing the vertical space for the
            canvas. Desktop keeps this exact bar, unchanged. ────────────── */}
        {!isMobile && (
        <div className="editor-topbar">

          {/* Back */}
          <button
            type="button"
            onClick={() => guardedNavigate('/projects')}
            className="editor-back-btn"
          >
            <i className="bi bi-arrow-left" style={{ marginRight: 6 }} />
            Projects
          </button>

          {/* Breadcrumb: chevron + project name */}
          <i className="bi bi-chevron-right" style={{ fontSize: 11, color: '#cbd5e1' }} />
          <span className="editor-project-name">{topbarLabel}</span>

          {/* Video guides — desktop widths only (this whole bar is hidden on phones,
              where Configure keeps its own copies in the canvas toolbar). */}
          <div className="editor-topbar-divider" />
          <div className="editor-guide-group">
            <span className="editor-guide-caption">Video Guides</span>
            {[
              ['desktop', 'Desktop Video Guide', 'Desktop'],
              ['mobile', 'Mobile Video Guide', 'Mobile'],
              ['floor-plan-maker', 'Floor Plan Maker Tutorial Video', 'Floor Plan Maker'],
            ].map(([kind, title, label]) => (
              <button
                key={kind}
                type="button"
                title={title}
                onClick={() => openVideo(kind)}
                className="editor-guide-btn"
              >
                <VideoGuideIcon size={14} />
                {label}
              </button>
            ))}
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Unsaved changes badge */}
          {editorDirty && activeTab === 'configure' && (
            <span className="editor-unsaved-badge">
              <span className="editor-unsaved-dot" />
              Unsaved changes
            </span>
          )}

          {/* Save button — only shown when on configure tab */}
          {activeTab === 'configure' && (
            <button
              type="button"
              className="editor-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              <i className="bi bi-floppy" style={{ marginRight: 6 }} />
              Save
            </button>
          )}

          {/* Export — opens a small popover right here with "Export as PDF" / "Export as Word" */}
          {activeTab === 'configure' && (
            <div className="editor-export-wrap" ref={exportWrapRef}>
              <button
                type="button"
                className="editor-export-btn"
                onClick={() => setExportOpen((v) => !v)}
                disabled={exportBusy}
                aria-haspopup="menu"
                aria-expanded={exportOpen}
              >
                <i className="bi bi-box-arrow-up" style={{ marginRight: 6 }} />
                {exportBusy ? 'Exporting…' : 'Export'}
                <i className={`bi bi-chevron-${exportOpen ? 'up' : 'down'}`} style={{ marginLeft: 8, fontSize: 10 }} />
              </button>
              {exportOpen && (
                <div className="editor-export-popover" role="menu">
                  <button type="button" role="menuitem" className="editor-export-item" onClick={() => runExport('pdf')}>
                    <span className="editor-export-icon pdf"><i className="bi bi-file-earmark-pdf-fill" /></span>
                    <span className="editor-export-text">
                      <strong>Export as PDF</strong>
                      <small>Download the report (.pdf)</small>
                    </span>
                  </button>
                  <button type="button" role="menuitem" className="editor-export-item" onClick={() => runExport('word')}>
                    <span className="editor-export-icon word"><i className="bi bi-file-earmark-word-fill" /></span>
                    <span className="editor-export-text">
                      <strong>Export as Word</strong>
                      <small>Editable text, same look (.docx)</small>
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        <div className="tab-content">
          {renderActiveComponent()}
        </div>

        {/* Desktop only — mobile's equivalent (Account details) now lives
            inside Configure's own bottom toolbar + sheet, opened from a
            settings icon there instead of a separate floating button. */}
        {!isMobile && <UserProfilePopover />}
      </div>

      <VideoGuideModal
        open={videoOpen}
        url={videoKind === 'mobile' ? VIDEO_GUIDE_MOBILE_URL : videoKind === 'floor-plan-maker' ? FLOOR_PLAN_MAKER_TUTORIAL_URL : VIDEO_GUIDE_DESKTOP_URL}
        onClose={() => setVideoOpen(false)}
      />

      {/* ── Unsaved Changes Modal ──────────────────────────────────────────────
          Mobile: same bottom-sheet pattern as every other mobile modal in
          the editor (MobileSheetWrapper) instead of the centered popup —
          per explicit request. Desktop keeps the original .ucm-overlay/
          .ucm-card markup byte-for-byte unchanged below. */}
      {showUnsavedModal && (isMobile ? (
        <MobileSheetWrapper
          isMobile={isMobile}
          open={showUnsavedModal}
          onClose={() => { pendingNavRef.current = null; setShowUnsavedModal(false); }}
          title="Unsaved Changes"
        >
          <div style={{ padding: '4px 18px 22px' }}>
            <p style={{ fontSize: 13.5, color: '#475569', lineHeight: 1.55, margin: '0 0 20px' }}>
              You have unsaved changes in this project. If you leave now, your latest edits will be lost.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => {
                  setShowUnsavedModal(false);
                  if (saveRef.current?.setAfterSaveCallback && pendingNavRef.current) {
                    const nav = pendingNavRef.current;
                    pendingNavRef.current = null;
                    saveRef.current.setAfterSaveCallback(nav);
                  }
                  if (activeTab !== 'configure') setActiveTab('configure');
                  setTimeout(() => saveRef.current?.save(), activeTab !== 'configure' ? 120 : 0);
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: '#00A19D', color: '#fff', border: 'none', borderRadius: 10,
                  padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}
              >
                <i className="bi bi-floppy" />
                Save &amp; Leave
              </button>
              <button
                onClick={() => {
                  setShowUnsavedModal(false);
                  const nav = pendingNavRef.current;
                  pendingNavRef.current = null;
                  isLeavingRef.current = true;
                  if (nav) nav();
                }}
                style={{
                  background: '#FFF1F0', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 10,
                  padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}
              >
                Discard &amp; Leave
              </button>
              <button
                onClick={() => { pendingNavRef.current = null; setShowUnsavedModal(false); }}
                style={{
                  background: '#F1F5F9', color: '#334155', border: 'none', borderRadius: 10,
                  padding: '13px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Stay on Page
              </button>
            </div>
          </div>
        </MobileSheetWrapper>
      ) : (
        <div className="ucm-overlay">
          <div className="ucm-card">
            <div className="ucm-icon-wrap">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 className="ucm-title">Unsaved Changes</h3>
            <p className="ucm-body">
              You have unsaved changes in this project. If you leave now, your latest edits will be lost.
            </p>
            <div className="ucm-actions">
              <button
                className="ucm-btn ucm-btn-save"
                onClick={() => {
                  setShowUnsavedModal(false);
                  // After save completes → run the pending navigation
                  if (saveRef.current?.setAfterSaveCallback && pendingNavRef.current) {
                    const nav = pendingNavRef.current;
                    pendingNavRef.current = null;
                    saveRef.current.setAfterSaveCallback(nav);
                  }
                  // Trigger the save modal in Configure
                  if (activeTab !== 'configure') setActiveTab('configure');
                  setTimeout(() => saveRef.current?.save(), activeTab !== 'configure' ? 120 : 0);
                }}
              >
                <i className="bi bi-floppy" style={{ marginRight: 5 }} />
                Save &amp; Leave
              </button>
              <button
                className="ucm-btn ucm-btn-discard"
                onClick={() => {
                  setShowUnsavedModal(false);
                  const nav = pendingNavRef.current;
                  pendingNavRef.current = null;
                  isLeavingRef.current = true;
                  if (nav) nav();
                }}
              >
                Discard &amp; Leave
              </button>
              <button
                className="ucm-btn ucm-btn-cancel"
                onClick={() => {
                  pendingNavRef.current = null;
                  setShowUnsavedModal(false);
                }}
              >
                Stay on Page
              </button>
            </div>
          </div>
        </div>
      ))}

      <style jsx>{`
        .editor-container {
          background: #1a1a1a;
          height: 100%;
          display: flex;
          flex-direction: column;
          color: #ffffff;
          width: 100%;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          position: relative;
        }

        .editor-topbar {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0 16px;
          height: 52px;
          background: #ffffff;
          border-bottom: 1.5px solid #e2e8f0;
          box-shadow: 0 1px 6px rgba(0,0,0,0.06);
        }

        .editor-back-btn {
          background: transparent;
          color: #00A19D;
          border: 1.5px solid #00A19D;
          border-radius: 8px;
          height: 34px;
          padding: 0 14px;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
          transition: background 0.15s;
          flex-shrink: 0;
        }
        .editor-back-btn:hover {
          background: rgba(0, 161, 157, 0.07);
        }

        .editor-topbar-divider { width: 1px; height: 24px; background: #E2E8F0; margin: 0 6px; flex-shrink: 0; }
        .editor-guide-group { display: flex; align-items: center; gap: 8px; }
        .editor-guide-caption { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8A94A6; margin-right: 2px; white-space: nowrap; }
        .editor-guide-btn {
          height: 34px; padding: 0 12px; background: #E6F6F5; border: 1px solid #BFE6E4; color: #00857E;
          border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
          font-size: 12.5px; font-weight: 600; white-space: nowrap; transition: background 0.15s, border-color 0.15s;
        }
        .editor-guide-btn:hover { background: #D3F0EE; border-color: #00A19D; }

        .editor-project-name {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 260px;
        }

        /* Save + Export read as ONE split button: teal "Save" half, white "Export" half, shared outline + shadow */
        .editor-save-btn {
          display: inline-flex;
          align-items: center;
          background: #00A19D;
          color: #fff;
          border: 1.5px solid #00A19D;
          border-radius: 10px 0 0 10px;
          box-shadow: 0 3px 10px rgba(0,161,157,0.25);
          height: 38px;
          padding: 0 18px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s;
          flex-shrink: 0;
        }
        .editor-save-btn + .editor-export-wrap { margin-left: -10px; }

        .editor-export-wrap { position: relative; flex-shrink: 0; }
        .editor-export-btn {
          display: inline-flex; align-items: center; height: 38px; padding: 0 14px;
          background: #ffffff; color: #00857E; border: 1.5px solid #00A19D; border-left: none; border-radius: 0 10px 10px 0; box-shadow: 0 3px 10px rgba(0,161,157,0.25);
          font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s;
        }
        .editor-export-btn:hover:not(:disabled) { background: #E6F6F5; }
        .editor-export-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .editor-export-popover {
          position: absolute; top: calc(100% + 8px); right: 0; z-index: 2000; width: 250px;
          background: #ffffff; border: 1px solid #DCE0E8; border-radius: 12px; padding: 6px;
          box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
        }
        .editor-export-item {
          width: 100%; display: flex; align-items: center; gap: 12px; padding: 10px; border: none;
          background: transparent; border-radius: 8px; cursor: pointer; text-align: left; color: #0f172a;
        }
        .editor-export-item:hover { background: #F4FBFB; }
        .editor-export-icon {
          width: 36px; height: 36px; border-radius: 9px; display: flex; align-items: center; justify-content: center;
          font-size: 18px; color: #ffffff; flex-shrink: 0;
        }
        .editor-export-icon.pdf { background: #FF6B5C; }
        .editor-export-icon.word { background: #2B579A; }
        .editor-export-text { display: flex; flex-direction: column; line-height: 1.25; }
        .editor-export-text strong { font-size: 13px; font-weight: 700; }
        .editor-export-text small { font-size: 11px; color: #64748b; font-weight: 500; }

        .editor-save-btn:hover:not(:disabled) {
          background: #00857E;
        }
        .editor-save-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .tab-content {
          flex: 1;
          overflow: hidden;
          background: #1a1a1a;
        }

        /* ── Unsaved badge ─────────────────────────────────────── */
        .editor-unsaved-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: #fffbeb;
          border: 1px solid #fcd34d;
          border-radius: 20px;
          padding: 5px 12px;
          margin-right: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #92400e;
          white-space: nowrap;
          flex-shrink: 0;
          animation: ucmFadeIn 0.2s ease;
        }
        .editor-unsaved-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #f59e0b;
          flex-shrink: 0;
          animation: ucmPulse 1.4s ease-in-out infinite;
        }

        /* ── Unsaved Changes Modal ─────────────────────────────── */
        .ucm-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          backdrop-filter: blur(2px);
          animation: ucmFadeIn 0.15s ease;
        }
        .ucm-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 36px 32px 28px;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.1);
          text-align: center;
          animation: ucmSlideUp 0.2s ease;
        }
        .ucm-icon-wrap {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: #fef3c7;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }
        .ucm-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 10px;
        }
        .ucm-body {
          font-size: 13.5px;
          color: #475569;
          line-height: 1.55;
          margin: 0 0 24px;
        }
        .ucm-actions {
          display: flex;
          flex-direction: column;
          gap: 9px;
        }
        .ucm-btn {
          width: 100%;
          padding: 11px 16px;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: opacity 0.15s, transform 0.1s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .ucm-btn:hover { opacity: 0.88; transform: translateY(-1px); }
        .ucm-btn:active { transform: translateY(0); }
        .ucm-btn-save {
          background: #00A19D;
          color: #ffffff;
        }
        .ucm-btn-discard {
          background: #fee2e2;
          color: #991b1b;
        }
        .ucm-btn-cancel {
          background: #f1f5f9;
          color: #334155;
        }

        @keyframes ucmFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ucmSlideUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ucmPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.75); } }
      `}</style>
    </>
  );
}
